import { useState, useEffect } from "react";
import { getMunicipalExecutiveAPI, getSecretariatsAPI } from "../../services/api";
import ErrorMessage from "../ui_elements/ErrorMessage";
import defaultPhoto from "../../assets/imgs/default_photo.svg";

const OrganizationalStructure = () => {
  const [executives, setExecutives] = useState([]);
  const [secretariats, setSecretariats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [execRes, secRes] = await Promise.all([
          getMunicipalExecutiveAPI(),
          getSecretariatsAPI(),
        ]);
        setExecutives(execRes.data);
        setSecretariats(secRes.data);
        if (execRes.data.length > 0) {
          setSelected(execRes.data[0]);
          setSelectedType("executive");
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const select = (item, type) => {
    setSelected(item);
    setSelectedType(type);
  };

  const isActive = (item, type) =>
    selected?.id === item.id && selectedType === type;

  if (loading)
    return <div className="py-8 text-center text-gray-500">Carregando...</div>;
  if (error)
    return (
      <ErrorMessage
        title="Estrutura organizacional indisponível"
        description="Não foi possível carregar os dados. Nossa equipe já está trabalhando para restabelecer o acesso o mais breve possível."
      />
    );

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">

        {/* Sidebar */}
        <aside className="lg:w-72 flex-shrink-0 space-y-6">

          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-3 border-b pb-2">
              Gestão Municipal
            </h2>
            <ul className="space-y-2">
              {executives.map((exec) => (
                <li key={exec.id}>
                  <button
                    onClick={() => select(exec, "executive")}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive(exec, "executive")
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                  >
                    <span className="block">{exec.name}</span>
                    <span
                      className={`text-xs font-normal ${
                        isActive(exec, "executive") ? "text-blue-100" : "text-gray-400"
                      }`}
                    >
                      {exec.role}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-3 border-b pb-2">
              Secretarias Municipais
            </h2>
            <ul className="space-y-2">
              {secretariats.map((sec) => (
                <li key={sec.id}>
                  <button
                    onClick={() => select(sec, "secretariat")}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive(sec, "secretariat")
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                  >
                    {sec.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

        </aside>

        {/* Detail panel */}
        <main className="flex-1 min-w-0">
          {!selected && (
            <p className="text-gray-400 text-center mt-16">
              Selecione um item na lista para ver os detalhes.
            </p>
          )}

          {selected && selectedType === "executive" && (
            <div>
              <div className="flex flex-col sm:flex-row items-start gap-6 bg-gray-50 rounded-2xl p-6 mb-8 shadow-sm">
                <img
                  src={selected.photo || defaultPhoto}
                  alt={selected.name}
                  onError={(e) => { e.target.src = defaultPhoto; e.target.onerror = null; }}
                  className="w-36 h-36 object-cover rounded-xl flex-shrink-0"
                />
                <div className="flex flex-col justify-center">
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">
                    {selected.name}
                  </h1>
                  <p className="text-lg text-gray-500">{selected.role}</p>
                </div>
              </div>
            </div>
          )}

          {selected && selectedType === "secretariat" && (
            <div>
              <div className="flex flex-col sm:flex-row items-start gap-6 bg-gray-50 rounded-2xl p-6 mb-8 shadow-sm">
                <img
                  src={selected.photo || defaultPhoto}
                  alt={selected.name}
                  onError={(e) => { e.target.src = defaultPhoto; e.target.onerror = null; }}
                  className="w-36 h-28 object-cover rounded-xl flex-shrink-0"
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">
                    {selected.name}
                  </h1>
                  <p className="text-lg text-gray-600 mb-1">{selected.responsible}</p>
                  {selected.email && (
                    <a
                      href={`mailto:${selected.email}`}
                      className="text-blue-500 hover:underline text-sm"
                    >
                      {selected.email}
                    </a>
                  )}
                </div>
              </div>

              {selected.sections?.length > 0 && (
                <div className="space-y-4">
                  {selected.sections.map((section) => (
                    <details
                      key={section.id}
                      className="border rounded-2xl p-5"
                      open
                    >
                      <summary className="text-lg font-semibold text-gray-800 cursor-pointer">
                        {section.title}
                      </summary>
                      <ul className="mt-4 space-y-2 text-gray-600 list-disc list-inside">
                        {section.content.map((item, index) => (
                          <li key={index} className="text-sm leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>

      </div>
    </div>
  );
};

export default OrganizationalStructure;
